import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { RunInZone } from '../../_shared/utils/run-in-zone';

export type SeatSelectionStateModel = 'none' | 'selected' | 'confirmed' | 'list-view' | 'filter' | '3d-view';

/**
 * SeatSelectionState is an injectable service that manages the state of seat selection in an application context.
 * It provides methods to set, reset, and retrieve the current state of seat selection. The service uses
 * BehaviorSubject to manage and emit changes to the seat selection state. It supports various states such as
 * none, selected, confirmed, list-view, filter, and 3d-view, each representing a different stage or mode of seat selection.
 */
@Injectable({
  providedIn: 'root',
})
export class SeatSelectionState {
  public ngZone = inject(NgZone);
  private readonly _previousState = new BehaviorSubject<SeatSelectionStateModel>('none');
  private readonly _state = new BehaviorSubject<SeatSelectionStateModel>('none');
  public readonly previousState$ = this._previousState.asObservable();
  public readonly state$ = this._state.asObservable();

  /**
   * Retrieves the previous seat selection state.
   * @returns The previous state.
   */
  public getPreviousState = () => {
    return this._previousState.getValue();
  };

  /**
   * Retrieves the current seat selection state.
   * @returns The current state.
   */
  public getState = () => {
    return this._state.getValue();
  };

  /**
   * Sets the previous state by emitting the current state.
   *
   * @remarks
   * This method is used to update the previous state by emitting the current state.
   *
   * @author Jonathan Alvarado
   */
  @RunInZone()
  public setPreviousState() {
    this._previousState.next(this.getState());
  }

  /**
   * Updates the state to a new specified state.
   * @param newState The new state to transition to.
   */
  @RunInZone()
  public setState(newState: SeatSelectionStateModel) {
    this.setPreviousState();
    this._state.next(newState);
  }

  /**
   * Resets the seat selection state to 'none'.
   */
  public resetState = () => {
    this.setPreviousState();
    this.setState('none');
  };

  /**
   * Sets the seat selection state to 'selected'.
   */
  public setSelectState = () => {
    this.setPreviousState();
    this.setState('selected');
  };

  /**
   * Sets the seat selection state to 'confirmed'.
   */
  public setConfirmState = () => {
    this.setPreviousState();
    this.setState('confirmed');
  };

  /**
   * Sets the seat selection state to 'list-view'.
   */
  public setListViewState = () => {
    this.setPreviousState();
    this.setState('list-view');
  };

  /**
   * Sets the seat selection state to '3d-view'.
   */
  public setViewer3DState = () => {
    this.setPreviousState();
    this.setState('3d-view');
  };
}
